// host-onboarding.js
import isEqual from "lodash/isEqual";
import { OPERATING_SYSTEMS } from "onboarding/host/installTypes";

const PERSIST_TO_COLUMN = "otherTechnology";

export const INITIAL_STATE = {
  easyInstructionsAvailable: false,
  installType: null,
  manual: false,
  connected: false,
  countdown: 20,
  timerStarted: false,
  initialized: false,
};

// helpers
export const serializeHostState = (state = {}, compareTo = {}) => {
  // serialize to string for saving to onboarding state
  const {
    connected,
    countdown,
    installType,
    manual,
    timerStarted,
    initialized,
    reset,
  } = state;
  if (reset) return { [PERSIST_TO_COLUMN]: null };
  if (!initialized) return {};
  const status = connected
    ? `${timerStarted ? countdown : "connected"}`
    : "waiting";
  const stateString = `${installType || ""}:${manual ? 1 : 0}:${status}`;
  if (isEqual(stateString, compareTo[PERSIST_TO_COLUMN])) {
    return {};
  }
  return { [PERSIST_TO_COLUMN]: stateString };
};

export const hostStateEmpty = onboardingState =>
  !(onboardingState || {})[PERSIST_TO_COLUMN];

const isEasyInstructionsAvailable = installType => {
  const isWindows = installType === "windows";
  const isKubernetes = installType === "kubernetes";
  return !!OPERATING_SYSTEMS[installType] && !isWindows && !isKubernetes;
};

export const deserializeHostState = onboardingState => {
  // deserialize from string read from onboarding state
  const serializedState = (onboardingState || {})[PERSIST_TO_COLUMN] || "";
  const [installType = "", manual, status] = serializedState.split(":");
  const timerStarted = /^\d+$/.test(status);
  const connected = timerStarted || status === "connected";
  const countdown = timerStarted ? Number(status) : INITIAL_STATE.countdown;
  return {
    ...INITIAL_STATE,
    connected,
    countdown,
    installType,
    manual: manual === "1",
    easyInstructionsAvailable: isEasyInstructionsAvailable(installType),
    timerStarted,
  };
};

// action types
const SET_HOST_ONBOARDING_STATE =
  "appoptics/host-onboarding/SET_HOST_ONBOARDING_STATE";
const SET_INSTALL_TYPE = "appoptics/host-onboarding/SET_INSTALL_TYPE";
const SHOW_MANUAL = "appoptics/host-onboarding/SHOW_MANUAL";
const SET_CONNECTED = "appoptics/host-onboarding/SET_CONNECTED";
const TICK = "appoptics/host-onboarding/TICK";
const INVALIDATE = "appoptics/host-onboarding/INVALIDATE";

// action creators
export const setHostOnboardingState = onboardingState => {
  return {
    type: SET_HOST_ONBOARDING_STATE,
    onboardingState,
  };
};

export const setInstallType = installType => {
  return {
    type: SET_INSTALL_TYPE,
    installType,
  };
};

export const showManualInstructions = () => {
  return {
    type: SHOW_MANUAL,
    manual: true,
  };
};

export const showEasyInstructions = () => {
  return {
    type: SHOW_MANUAL,
    manual: false,
  };
};

export const setConnected = (connected = true) => {
  return {
    type: SET_CONNECTED,
    connected,
  };
};

export const tick = () => {
  return {
    type: TICK,
  };
};

export const invalidateHostState = () => ({
  type: INVALIDATE,
});

// reducer
const hostOnboardingReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_HOST_ONBOARDING_STATE:
      return {
        ...state,
        ...deserializeHostState(action.onboardingState),
        initialized: true,
      };
    case SET_INSTALL_TYPE: {
      return {
        ...state,
        easyInstructionsAvailable: isEasyInstructionsAvailable(
          action.installType
        ),
        installType: action.installType,
        initialized: true,
      };
    }
    case SHOW_MANUAL:
      return {
        ...state,
        manual: action.manual,
        initialized: true,
      };
    case SET_CONNECTED:
      return {
        ...state,
        connected: action.connected,
      };
    case TICK:
      return {
        ...state,
        countdown: state.countdown - 1,
        timerStarted: true,
      };
    case INVALIDATE:
      return {
        ...state,
        initialized: false,
      };
    default:
      return state;
  }
};

export default hostOnboardingReducer;
